/**
 * GlobalTools Runtime Engine
 *
 * Executes tool definitions by managing step transitions, state, and messaging.
 * Handles the full lifecycle: initialization, step execution, user input handling,
 * and completion/cleanup.
 */

import { EventEmitter } from './eventEmitter';
import { createMessagingHelpers } from './messaging';
import type {
  GlobalToolDefinition,
  StepDefinition,
  StepContext,
  StepResult,
  MessagingHelpers,
  SharedWorkflowContext,
  AITransactionRecorder,
  WorkflowHelpersInterface,
  GlobalToolId,
  StepStatus,
  ChatMessage,
} from './types';

// ============================================================================
// Event Types
// ============================================================================

/**
 * Runtime lifecycle event types.
 * Used for workflow chaining and completion tracking.
 */
export type RuntimeEvent =
  | 'step-started'
  | 'step-completed'
  | 'step-failed'
  | 'awaiting-input'
  | 'workflow-completed'
  | 'workflow-failed'
  | 'workflow-cancelled';

/**
 * Data passed with runtime events.
 */
export interface RuntimeEventData {
  toolId: GlobalToolId;
  stepId: string;
  state: unknown;
  output?: unknown;
  error?: Error;
}

// ============================================================================
// Tool Runtime State
// ============================================================================

/**
 * Internal state of a running tool instance.
 */
interface InternalRuntimeState<TState> {
  /** Current tool state */
  state: TState;
  /** Currently executing step ID (null if idle) */
  currentStepId: string | null;
  /** Last trigger message that started or resumed the tool */
  lastTriggerMessage: ChatMessage | null;
  /** Abort controller for cancellation */
  abortController: AbortController;
  /** Whether this step is awaiting user input */
  isAwaitingInput: boolean;
  /** Timestamp when awaiting-input state began (for diagnostics and recovery) */
  awaitingInputSince: number | null;
  /** Data to carry forward to next step */
  carryForward: Record<string, unknown>;
  /** Step execution history */
  history: Array<{
    stepId: string;
    status: StepStatus;
    timestamp: Date;
    output?: unknown;
    error?: Error;
  }>;
}

// ============================================================================
// Global Tool Runtime Class
// ============================================================================

/**
 * Runtime engine that executes a single tool.
 * Each tool instance gets its own runtime for state isolation.
 */
export class GlobalToolRuntime<TState = unknown> {
  private tool: GlobalToolDefinition<TState>;
  private internalState: InternalRuntimeState<TState>;
  private messaging: MessagingHelpers | null = null;
  private sharedContext: SharedWorkflowContext;
  private recordTransaction: AITransactionRecorder;
  private helpersRef: { current: WorkflowHelpersInterface | null };
  private eventEmitter = new EventEmitter();

  constructor(
    tool: GlobalToolDefinition<TState>,
    sharedContext: SharedWorkflowContext,
    recordTransaction: AITransactionRecorder,
    helpersRef: { current: WorkflowHelpersInterface | null }
  ) {
    this.tool = tool;
    this.sharedContext = sharedContext;
    this.recordTransaction = recordTransaction;
    this.helpersRef = helpersRef;

    // Initialize internal state
    this.internalState = {
      state: tool.createInitialState(),
      currentStepId: null,
      lastTriggerMessage: null,
      abortController: new AbortController(),
      isAwaitingInput: false,
      awaitingInputSince: null,
      carryForward: {},
      history: [],
    };
  }

  // ==========================================================================
  // Public Getters
  // ==========================================================================

  /**
   * Get the tool definition.
   */
  get definition(): GlobalToolDefinition<TState> {
    return this.tool;
  }

  /**
   * Get the tool ID.
   */
  get id(): GlobalToolId {
    return this.tool.id;
  }

  /**
   * Get current tool state.
   */
  getState(): TState {
    return this.internalState.state;
  }

  /**
   * Get current step ID (null if idle).
   */
  getCurrentStep(): string | null {
    return this.internalState.currentStepId;
  }

  /**
   * Check if tool is currently running.
   */
  isRunning(): boolean {
    return this.internalState.currentStepId !== null;
  }

  /**
   * Check if tool is awaiting user input.
   */
  isAwaitingInput(): boolean {
    return this.internalState.isAwaitingInput;
  }

  /**
   * Get how long the tool has been awaiting input (ms), or null if not awaiting.
   */
  getAwaitingInputDuration(): number | null {
    if (!this.internalState.isAwaitingInput || !this.internalState.awaitingInputSince) {
      return null;
    }
    return Date.now() - this.internalState.awaitingInputSince;
  }

  /**
   * Update the shared workflow context reference.
   * Called when React re-creates the sharedContext object via useMemo,
   * ensuring runtimes always use the latest context (not a stale capture).
   */
  updateSharedContext(newContext: SharedWorkflowContext): void {
    this.sharedContext = newContext;
  }

  /**
   * Get step execution history.
   */
  getHistory() {
    return [...this.internalState.history];
  }

  // ==========================================================================
  // Event System
  // ==========================================================================

  /**
   * Add event listener for runtime events.
   * Used for workflow chaining and completion tracking.
   */
  on(event: RuntimeEvent, listener: (data: RuntimeEventData) => void): void {
    this.eventEmitter.on(event, listener);
  }

  /**
   * Remove event listener.
   */
  off(event: RuntimeEvent, listener: (data: RuntimeEventData) => void): void {
    this.eventEmitter.off(event, listener);
  }

  /**
   * Emit runtime event.
   * @private
   */
  private emit(event: RuntimeEvent, data: RuntimeEventData): void {
    console.log(`[Runtime] ${this.tool.id} emitting event: ${event}`);
    this.eventEmitter.emit(event, data);
  }

  // ==========================================================================
  // State Persistence & Restoration
  // ==========================================================================

  /**
   * Restore serialized state into the runtime, allowing button clicks to resume
   * a workflow that was interrupted by page reload / tab close.
   *
   * Only the essential fields are restored — messaging helpers and abort controllers
   * are recreated fresh. The runtime enters awaiting-input mode so the next
   * user interaction (button click or text) triggers resume().
   */
  restoreState(state: TState, stepId: string): void {
    console.log(`[Runtime] ${this.tool.id} restoring persisted state for step "${stepId}"`);
    this.internalState.state = state;
    this.internalState.currentStepId = stepId;
    this.internalState.isAwaitingInput = true;
    this.internalState.awaitingInputSince = Date.now();
    this.internalState.abortController = new AbortController(); // Fresh controller
  }

  // ==========================================================================
  // Lifecycle Methods
  // ==========================================================================

  /**
   * Start tool execution from the entry point.
   */
  async start(triggerMessage: ChatMessage, initialInput?: unknown): Promise<void> {
    // GUARD: Prevent restart while actively running (e.g., double-click on skip button)
    if (this.isRunning() && !this.isAwaitingInput()) {
      console.error(
        `[GlobalToolRuntime] ${this.tool.id} start() called while actively running step "${this.internalState.currentStepId}" — refusing to restart`
      );
      return;
    }

    // Ensure we have helpers
    if (!this.helpersRef.current) {
      console.error(`[GlobalToolRuntime] Cannot start ${this.tool.id}: no helpers available`);
      return;
    }

    // Create messaging helpers (pass sharedContext for pending token accumulator)
    this.messaging = createMessagingHelpers(this.helpersRef.current, this.sharedContext);

    // Reset state for fresh execution
    this.internalState = {
      state: this.tool.createInitialState(),
      currentStepId: null,
      lastTriggerMessage: triggerMessage,
      abortController: new AbortController(),
      isAwaitingInput: false,
      awaitingInputSince: null,
      carryForward: {},
      history: [],
    };

    // Determine entry step
    const entryStepId = this.tool.entryStep || this.tool.steps[0]?.id;
    if (!entryStepId) {
      console.error(`[GlobalToolRuntime] Tool ${this.tool.id} has no steps defined`);
      return;
    }

    // Execute entry step
    await this.executeStep(entryStepId, initialInput);
  }

  /**
   * Resume execution from awaiting-input state with user response.
   * Supports recovery: if isAwaitingInput was lost but currentStepId exists,
   * force-resumes to prevent the workflow from hanging permanently.
   */
  async resume(
    userInput: string,
    userAttachments?: File[],
    newTriggerMessage?: ChatMessage
  ): Promise<void> {
    console.log(
      `[GlobalToolRuntime] ${this.tool.id} resume() called — isAwaitingInput=${this.internalState.isAwaitingInput}, currentStepId=${this.internalState.currentStepId}`
    );

    // Update trigger message if provided
    if (newTriggerMessage) {
      this.internalState.lastTriggerMessage = newTriggerMessage;
    }

    // Normal path: awaiting input with a current step
    if (this.internalState.isAwaitingInput && this.internalState.currentStepId) {
      // Ensure we have helpers
      if (!this.helpersRef.current) {
        console.error(`[GlobalToolRuntime] Cannot resume ${this.tool.id}: no helpers available`);
        return;
      }

      // Re-create messaging helpers (may have changed, pass sharedContext for pending token accumulator)
      this.messaging = createMessagingHelpers(this.helpersRef.current, this.sharedContext);

      // Create fresh AbortController for the resumed execution.
      // The original controller from start() may be stale after a long idle period.
      // This ensures no accumulated abort listeners or edge-case aborted state.
      this.internalState.abortController = new AbortController();

      // Clear awaiting input flag
      this.internalState.isAwaitingInput = false;
      this.internalState.awaitingInputSince = null;

      // Re-execute current step with user input
      await this.executeStep(this.internalState.currentStepId, {
        ...this.internalState.carryForward,
        __userInput: userInput,
        __userAttachments: userAttachments,
      });
      return;
    }

    // Recovery path: isAwaitingInput was lost but we still have a currentStepId.
    // This can happen if React re-renders or sharedContext changes caused state desync.
    if (!this.internalState.isAwaitingInput && this.internalState.currentStepId) {
      // GUARD: Don't force-resume if actively executing (abort controller not aborted).
      // Only use recovery when the workflow is genuinely stuck, not when a step is running.
      if (!this.internalState.abortController.signal.aborted) {
        console.warn(
          `[GlobalToolRuntime] ${this.tool.id} resume() called while actively executing step "${this.internalState.currentStepId}" — ignoring to prevent parallel execution`
        );
        return;
      }

      console.warn(
        `[GlobalToolRuntime] ${this.tool.id} RECOVERY: isAwaitingInput=false but currentStepId=${this.internalState.currentStepId}, aborted=true — force-resuming to prevent workflow hang`
      );

      if (!this.helpersRef.current) {
        console.error(`[GlobalToolRuntime] Cannot force-resume ${this.tool.id}: no helpers available`);
        return;
      }

      this.messaging = createMessagingHelpers(this.helpersRef.current, this.sharedContext);
      this.internalState.abortController = new AbortController();
      this.internalState.awaitingInputSince = null;

      await this.executeStep(this.internalState.currentStepId, {
        ...this.internalState.carryForward,
        __userInput: userInput,
        __userAttachments: userAttachments,
      });
      return;
    }

    // No recovery possible — no current step
    console.warn(
      `[GlobalToolRuntime] ${this.tool.id} not awaiting input and no currentStepId, ignoring resume`
    );
  }

  /**
   * Cancel current execution.
   */
  cancel(): void {
    // Abort any pending operations
    this.internalState.abortController.abort();

    // Run cleanup for current step if defined
    const currentStep = this.findStep(this.internalState.currentStepId);
    if (currentStep?.cleanup && this.messaging) {
      const context = this.buildStepContext(currentStep.id);
      currentStep.cleanup(context).catch((err) => {
        console.error(`[GlobalToolRuntime] Cleanup error for ${currentStep.id}:`, err);
      });
    }

    // Record cancellation in history
    if (this.internalState.currentStepId) {
      this.internalState.history.push({
        stepId: this.internalState.currentStepId,
        status: 'cancelled',
        timestamp: new Date(),
      });

      // Emit workflow cancelled event
      this.emit('workflow-cancelled', {
        toolId: this.tool.id,
        stepId: this.internalState.currentStepId,
        state: this.internalState.state,
      });
    }

    // Reset to idle
    this.reset();
  }

  /**
   * Reset to initial state (called after completion or cancellation).
   */
  reset(): void {
    console.log(
      `[GlobalToolRuntime] ${this.tool.id} reset() called — was: currentStepId=${this.internalState.currentStepId}, isAwaitingInput=${this.internalState.isAwaitingInput}`
    );
    this.internalState = {
      state: this.tool.createInitialState(),
      currentStepId: null,
      lastTriggerMessage: null,
      abortController: new AbortController(),
      isAwaitingInput: false,
      awaitingInputSince: null,
      carryForward: {},
      history: this.internalState.history, // Preserve history
    };
  }

  // ==========================================================================
  // Step Execution
  // ==========================================================================

  /**
   * Execute a specific step.
   */
  private async executeStep(stepId: string, input?: unknown): Promise<void> {
    const step = this.findStep(stepId);
    if (!step) {
      console.error(`[GlobalToolRuntime] Step "${stepId}" not found in tool "${this.tool.id}"`);
      this.reset();
      return;
    }

    if (!this.messaging) {
      console.error(`[GlobalToolRuntime] No messaging helpers available`);
      this.reset();
      return;
    }

    // Set current step
    this.internalState.currentStepId = stepId;

    // Extract user input from carry-forward if present
    const userInput = (input as Record<string, unknown>)?.__userInput as string | undefined;
    const userAttachments = (input as Record<string, unknown>)?.__userAttachments as File[] | undefined;

    // Clean input for step (remove internal fields)
    const cleanedInput = input && typeof input === 'object' ? { ...input } : input;
    if (cleanedInput && typeof cleanedInput === 'object') {
      delete (cleanedInput as Record<string, unknown>).__userInput;
      delete (cleanedInput as Record<string, unknown>).__userAttachments;
    }

    // Validate input if validator exists
    if (step.validateInput && cleanedInput !== undefined) {
      const validation = step.validateInput(cleanedInput);
      if (!validation.valid) {
        this.messaging.showError('Validation Error', validation.error || 'Invalid input');
        this.recordStepResult(stepId, 'failed', undefined, new Error(validation.error));
        this.reset();
        return;
      }
    }

    // Build step context
    const context = this.buildStepContext(stepId, userInput, userAttachments);

    // Record step start
    this.internalState.history.push({
      stepId,
      status: 'running',
      timestamp: new Date(),
    });

    try {
      // Execute the step
      const result = await step.execute(context, cleanedInput);

      // Process result
      await this.processStepResult(step, result);
    } catch (error) {
      // Handle execution error
      console.error(`[GlobalToolRuntime] Step ${stepId} error:`, error);

      // Run cleanup if defined
      if (step.cleanup) {
        try {
          await step.cleanup(context);
        } catch (cleanupErr) {
          console.error(`[GlobalToolRuntime] Cleanup error:`, cleanupErr);
        }
      }

      // Show error message
      this.messaging.showError(
        'Execution Error',
        (error as Error).message || 'An unexpected error occurred'
      );

      // Record failure
      this.recordStepResult(stepId, 'failed', undefined, error as Error);

      // Reset
      this.reset();
    }
  }

  /**
   * Process the result of a step execution.
   */
  private async processStepResult(
    step: StepDefinition<TState>,
    result: StepResult
  ): Promise<void> {
    switch (result.status) {
      case 'completed': {
        // Record completion
        this.recordStepResult(step.id, 'completed', result.output);

        // Emit step completion event
        this.emit('step-completed', {
          toolId: this.tool.id,
          stepId: step.id,
          state: this.internalState.state,
          output: result.output,
        });

        // Determine next step
        const nextStepId = result.nextStep !== undefined
          ? result.nextStep
          : this.getNextStepId(step.id);

        // Store carry-forward data
        if (result.carryForward) {
          this.internalState.carryForward = {
            ...this.internalState.carryForward,
            ...result.carryForward,
          };
        }

        if (nextStepId) {
          // Continue to next step
          await this.executeStep(nextStepId, this.internalState.carryForward);
        } else {
          // Tool completed - emit workflow completion event
          this.emit('workflow-completed', {
            toolId: this.tool.id,
            stepId: step.id,
            state: this.internalState.state,
            output: result.output,
          });

          // Reset to idle
          this.internalState.currentStepId = null;
          this.internalState.isAwaitingInput = false;
          this.internalState.awaitingInputSince = null;
        }
        break;
      }

      case 'awaiting-input': {
        // Stay on current step, waiting for user input
        this.internalState.isAwaitingInput = true;
        this.internalState.awaitingInputSince = Date.now();
        console.log(
          `[GlobalToolRuntime] ${this.tool.id} entering awaiting-input for step "${step.id}" — user can respond at any time (no timeout)`
        );

        // Emit awaiting-input event
        this.emit('awaiting-input', {
          toolId: this.tool.id,
          stepId: step.id,
          state: this.internalState.state,
          output: result.output,
        });

        // Store carry-forward data for when user responds
        if (result.carryForward) {
          this.internalState.carryForward = {
            ...this.internalState.carryForward,
            ...result.carryForward,
          };
        }

        // Record awaiting state
        this.recordStepResult(step.id, 'awaiting-input', result.output);
        break;
      }

      case 'failed': {
        // Run cleanup if defined
        if (step.cleanup && this.messaging) {
          const context = this.buildStepContext(step.id);
          try {
            await step.cleanup(context);
          } catch (cleanupErr) {
            console.error(`[GlobalToolRuntime] Cleanup error:`, cleanupErr);
          }
        }

        // Show error message (skip if step already displayed its own error to the user)
        if (this.messaging && result.error && !result.userFacingErrorShown) {
          this.messaging.showError('Step Failed', result.error.message);
        }

        // Emit workflow failed event
        this.emit('workflow-failed', {
          toolId: this.tool.id,
          stepId: step.id,
          state: this.internalState.state,
          error: result.error,
        });

        // Record failure
        this.recordStepResult(step.id, 'failed', undefined, result.error);

        // Reset
        this.reset();
        break;
      }
    }
  }

  // ==========================================================================
  // Helper Methods
  // ==========================================================================

  /**
   * Build the step context for execution.
   */
  private buildStepContext(
    stepId: string,
    userInput?: string,
    userAttachments?: File[]
  ): StepContext<TState> {
    return {
      state: this.internalState.state,
      setState: (partial: Partial<TState>) => {
        this.internalState.state = { ...this.internalState.state, ...partial };
      },
      messaging: this.messaging!,
      triggerMessage: this.internalState.lastTriggerMessage!,
      userInput,
      userAttachments,
      abortSignal: this.internalState.abortController.signal,
      recordTransaction: this.recordTransaction,
      sharedContext: this.sharedContext,
      toolId: this.tool.id,
      stepId,
    };
  }

  /**
   * Find a step by ID.
   */
  private findStep(stepId: string | null): StepDefinition<TState> | undefined {
    if (!stepId) return undefined;
    return this.tool.steps.find((s) => s.id === stepId);
  }

  /**
   * Get the next step ID in sequence.
   */
  private getNextStepId(currentId: string): string | null {
    const currentIndex = this.tool.steps.findIndex((s) => s.id === currentId);
    if (currentIndex === -1 || currentIndex >= this.tool.steps.length - 1) {
      return null;
    }
    return this.tool.steps[currentIndex + 1].id;
  }

  /**
   * Record a step result in history.
   */
  private recordStepResult(
    stepId: string,
    status: StepStatus,
    output?: unknown,
    error?: Error
  ): void {
    // Find and update the running entry, or add new one
    const lastEntry = this.internalState.history[this.internalState.history.length - 1];
    if (lastEntry && lastEntry.stepId === stepId && lastEntry.status === 'running') {
      lastEntry.status = status;
      lastEntry.output = output;
      lastEntry.error = error;
    } else {
      this.internalState.history.push({
        stepId,
        status,
        timestamp: new Date(),
        output,
        error,
      });
    }
  }
}

// ============================================================================
// Factory Function
// ============================================================================

/**
 * Create a new GlobalToolRuntime instance.
 */
export function createToolRuntime<TState>(
  tool: GlobalToolDefinition<TState>,
  sharedContext: SharedWorkflowContext,
  recordTransaction: AITransactionRecorder,
  helpersRef: { current: WorkflowHelpersInterface | null }
): GlobalToolRuntime<TState> {
  return new GlobalToolRuntime(tool, sharedContext, recordTransaction, helpersRef);
}
